import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Mail, Phone, MapPin, Send, Loader2, CheckCircle, AlertCircle,
  Linkedin, Youtube, Facebook, Twitter, Instagram, Github, Globe, Check, ArrowRight
} from 'lucide-react';
import { useSiteConfig, API_BASE, resolveUploadUrl } from '../utils/siteConfig';

const socialIconMap: Record<string, any> = {
  LinkedIn: Linkedin,
  YouTube: Youtube,
  Facebook: Facebook,
  Twitter: Twitter,
  Instagram: Instagram,
  GitHub: Github,
  Meta: Facebook
};

const brandColors: Record<string, { text: string; bg: string; border: string }> = {
  LinkedIn: { text: 'text-[#0077b5]', bg: 'hover:bg-[#0077b5]', border: 'hover:border-[#0077b5]' },
  YouTube: { text: 'text-[#ff0000]', bg: 'hover:bg-[#ff0000]', border: 'hover:border-[#ff0000]' },
  Facebook: { text: 'text-[#1877f2]', bg: 'hover:bg-[#1877f2]', border: 'hover:border-[#1877f2]' },
  Twitter: { text: 'text-[#1da1f2]', bg: 'hover:bg-[#1da1f2]', border: 'hover:border-[#1da1f2]' },
  Instagram: { text: 'text-[#e1306c]', bg: 'hover:bg-[#e1306c]', border: 'hover:border-[#e1306c]' },
  GitHub: { text: 'text-slate-800', bg: 'hover:bg-slate-800', border: 'hover:border-slate-800' },
  Meta: { text: 'text-[#0668e1]', bg: 'hover:bg-[#0668e1]', border: 'hover:border-[#0668e1]' },
};

const highlightServices = [
  'Digital Marketing Strategy',
  'Social Media Marketing',
  'Search Engine Optimization (SEO)',
  'Pay-Per-Click (PPC) Advertising',
  'Website Design & Development',
];

export function Contact() {
  const { config, openGetStarted } = useSiteConfig();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');
    try {
      const response = await fetch(`${API_BASE}/send-email.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message
        })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setStatus('success');
        setFormData({ firstName: '', lastName: '', email: '', phone: '', subject: '', message: '' });
      } else {
        setStatus('error');
        setErrorMessage(result.error || 'Failed to send message.');
      }
    } catch (err: any) {
      setStatus('error');
      setErrorMessage('A network error occurred. Please try again later.');
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans">

      {/* ─────────────────────────────────────────
          HERO: Contact info + Map overlay
      ───────────────────────────────────────── */}
      <section className="relative min-h-[500px] flex items-end overflow-hidden pt-24">
        {/* Background – theme gradient or custom image */}
        <div
          className="absolute inset-0"
          style={config.contact.backgroundImage ? {
            backgroundImage: `url(${resolveUploadUrl(config.contact.backgroundImage)})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
          } : {
            background: `linear-gradient(135deg, color-mix(in srgb, var(--primary) 90%, #000) 0%, color-mix(in srgb, var(--primary) 60%, #0a0a14) 60%, #0a0a14 100%)`
          }}
        />
        {/* Dark overlay for custom image readability */}
        {config.contact.backgroundImage && (
          <div className="absolute inset-0 bg-black/70 z-0 pointer-events-none" />
        )}
        {/* Dot texture overlay */}
        <div
          className="absolute inset-0 opacity-10 z-0"
          style={{
            backgroundImage: `radial-gradient(circle, rgba(255,255,255,0.4) 1px, transparent 1px)`,
            backgroundSize: '28px 28px'
          }}
        />
        {/* Glow blobs */}
        <div className="absolute top-10 right-1/3 w-80 h-80 rounded-full blur-3xl opacity-20 pointer-events-none"
          style={{ background: `var(--primary)` }} />

        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-8 pb-14 pt-10 flex flex-col lg:flex-row gap-10 items-start">
          {/* Left — contact details */}
          <motion.div
            className="flex-1 text-white"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-black mb-4 tracking-tight">Contact</h1>
            <p className="text-white/70 text-sm leading-relaxed max-w-sm mb-10">
              We'd love to hear from you! Whether you have questions, ideas, or projects in mind, our friendly team is always ready to connect, support, and grow together with you.
            </p>

            <div className="space-y-7">
              {/* Office */}
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 mt-0.5"
                  style={{ background: `color-mix(in srgb, var(--primary) 25%, transparent)` }}>
                  <MapPin size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/50 mb-0.5">Office</p>
                  <p className="text-white text-sm font-medium leading-relaxed">{config.contact.address}</p>
                </div>
              </div>

              {/* Phone */}
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: `color-mix(in srgb, var(--primary) 25%, transparent)` }}>
                  <Phone size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/50 mb-0.5">Phone</p>
                  <a href={`tel:${config.contact.phone}`} className="text-white text-sm font-medium hover:opacity-80 transition-opacity">
                    {config.contact.phone}
                  </a>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: `color-mix(in srgb, var(--primary) 25%, transparent)` }}>
                  <Mail size={16} className="text-white" />
                </div>
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-white/50 mb-0.5">Email</p>
                  <a href={`mailto:${config.contact.email}`} className="text-white text-sm font-medium hover:opacity-80 transition-opacity">
                    {config.contact.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Social */}
            <div className="mt-10">
              <p className="text-[10px] font-bold uppercase tracking-widest text-white/50 mb-3">Keep In Touch</p>
              <div className="flex gap-3">
                {config.contact.socials.map((social, i) => {
                  const Icon = socialIconMap[social.platform] || Globe;
                  const brandColorMap: Record<string, string> = {
                    LinkedIn: '#0077b5',
                    YouTube: '#ff0000',
                    Facebook: '#1877f2',
                    Twitter: '#1da1f2',
                    Instagram: '#e1306c',
                    GitHub: '#333333',
                    Meta: '#0668e1',
                  };
                  const color = brandColorMap[social.platform] || '#ffffff';
                  return (
                    <a
                      key={i}
                      href={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={social.platform}
                      className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 hover:-translate-y-1 hover:scale-110 hover:shadow-lg"
                      style={{
                        background: 'rgba(255,255,255,0.95)',
                        border: `2.5px solid ${color}`,
                        color: color,
                        boxShadow: `0 2px 8px ${color}33`
                      }}
                      onMouseEnter={e => {
                        (e.currentTarget as HTMLElement).style.background = color;
                        (e.currentTarget as HTMLElement).style.color = '#ffffff';
                      }}
                      onMouseLeave={e => {
                        (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.95)';
                        (e.currentTarget as HTMLElement).style.color = color;
                      }}
                    >
                      <Icon size={18} />
                    </a>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Right — embedded map */}
          <motion.div
            className="w-full lg:w-[420px] xl:w-[500px] rounded-2xl overflow-hidden shadow-2xl border border-white/10 shrink-0"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            style={{ height: '320px' }}
          >
            <iframe
              title="BigGroww Office Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3672.0!2d72.95!3d22.56!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x395e4e0!2sBig+Grow+Agency%2C+Anand%2C+Gujarat!5e0!3m2!1sen!2sin!4v1"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>
      </section>

      {/* ─────────────────────────────────────────
          FORM SECTION: Left info + Right form
      ───────────────────────────────────────── */}
      <section className="py-20 px-4 md:px-8 bg-white relative overflow-hidden">
        {/* Subtle dot background */}
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle, var(--primary) 1px, transparent 1px)`,
            backgroundSize: '24px 24px'
          }}
        />

        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-16 items-start">

            {/* Left — pitch text + services */}
            <motion.div
              initial={{ opacity: 0, x: -25 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: `var(--primary)` }}>
                Ready to work with me?
              </p>
              <h2 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight mb-5">
                "Fill the form and let's start creating something amazing together."
              </h2>
              <p className="text-slate-500 text-sm leading-relaxed mb-8">
                *Ready to get started? Simply fill out the form below — share your ideas, questions, or project details, and our team will connect with you quickly to help.
              </p>

              {/* Service checklist */}
              <ul className="space-y-3">
                {highlightServices.map((svc, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-700 font-medium">
                    <div
                      className="w-5 h-5 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: `color-mix(in srgb, var(--primary) 12%, transparent)` }}
                    >
                      <Check size={12} style={{ color: `var(--primary)` }} />
                    </div>
                    {svc}
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Right — contact form */}
            <motion.div
              initial={{ opacity: 0, x: 25 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* First / Last name */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                      First Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      required
                      value={formData.firstName}
                      onChange={handleChange}
                      disabled={status === 'loading'}
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-white focus:ring-2 focus:border-transparent outline-none transition-all text-sm text-slate-800 placeholder:text-slate-300"
                      style={{ '--tw-ring-color': `color-mix(in srgb, var(--primary) 30%, transparent)` } as any}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                      Last Name <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      required
                      value={formData.lastName}
                      onChange={handleChange}
                      disabled={status === 'loading'}
                      className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-white focus:ring-2 focus:border-transparent outline-none transition-all text-sm text-slate-800 placeholder:text-slate-300"
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                    Email <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    disabled={status === 'loading'}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-white focus:ring-2 focus:border-transparent outline-none transition-all text-sm text-slate-800"
                  />
                </div>

                {/* Subject */}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                    Subject <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="subject"
                    required
                    value={formData.subject}
                    onChange={handleChange}
                    disabled={status === 'loading'}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-white focus:ring-2 focus:border-transparent outline-none transition-all text-sm text-slate-800"
                  />
                </div>

                {/* Message */}
                <div>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">
                    Comment or Message <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    value={formData.message}
                    onChange={handleChange}
                    disabled={status === 'loading'}
                    className="w-full px-4 py-3 rounded-lg border border-slate-200 bg-white focus:ring-2 focus:border-transparent outline-none transition-all text-sm text-slate-800 resize-none"
                  />
                </div>

                {/* Status feedback */}
                <AnimatePresence mode="wait">
                  {status === 'success' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg text-sm"
                    >
                      <CheckCircle className="text-emerald-600 shrink-0" size={18} />
                      <span>Thank you! Your message has been sent successfully.</span>
                    </motion.div>
                  )}
                  {status === 'error' && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="flex items-center gap-3 p-4 bg-rose-50 border border-rose-200 text-rose-800 rounded-lg text-sm"
                    >
                      <AlertCircle className="text-rose-600 shrink-0" size={18} />
                      <span>{errorMessage}</span>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="flex items-center gap-2 px-8 py-3 rounded-lg text-white text-sm font-bold uppercase tracking-widest hover:opacity-90 hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:pointer-events-none shadow-md"
                  style={{ background: `var(--primary)` }}
                >
                  {status === 'loading' ? (
                    <><Loader2 className="animate-spin" size={16} /> Sending...</>
                  ) : (
                    <><Send size={16} /> Send Message</>
                  )}
                </button>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ─────────────────────────────────────────
          BOTTOM CTA
      ───────────────────────────────────────── */}
      <section
        className="py-20 px-4 md:px-8 text-center relative overflow-hidden"
        style={{ background: `linear-gradient(160deg, color-mix(in srgb, var(--primary) 4%, #f8faff), color-mix(in srgb, var(--primary) 4%, #f8faff))` }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-3xl opacity-10 pointer-events-none"
          style={{ background: `var(--primary)` }} />
        <motion.div
          className="relative z-10 max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-black text-slate-900 leading-tight mb-3">
            If You Have Any Question,<br />
            Feel to Call{' '}
            <a
              href={`tel:${config.contact.phone}`}
              className="bg-clip-text text-transparent"
              style={{ backgroundImage: `linear-gradient(135deg, var(--primary), var(--accent))` }}
            >
              {config.contact.phone}
            </a>
          </h2>
          <p className="text-slate-500 text-sm leading-relaxed mb-8 max-w-lg mx-auto">
            *If you have questions or need help, contact Big Grow — our team provides guidance, answers, and supports your business growth.
          </p>
          <button
            onClick={openGetStarted}
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg text-white text-sm font-bold uppercase tracking-widest hover:opacity-90 hover:-translate-y-0.5 transition-all shadow-lg cursor-pointer"
            style={{ background: `var(--primary)` }}
          >
            Let's Work Together <ArrowRight size={16} />
          </button>
        </motion.div>
      </section>

    </div>
  );
}
